import { gql } from '@apollo/client';
import { GetServerSideProps, NextPage } from 'next';
import { FOOTER_QUERY } from '../../components/footer';
import { Footer } from '../../components/footer';
import { HEADER_QUERY, Header } from '../../components/header';
import { ContentStackClient } from '../../lib/contentstack';
import {
  AllVertical,
  AllVerticalAlt,
  AllVerticalHub,
  VerticalHub,
} from '../../types/contentstack';
import { IPageProps } from '../../types/page';
import styled from 'styled-components';
import { mediaScreen } from '../../utils/mediaScreen';
import { ResponsiveContainer } from '../../components/responsive';
import {
  HeroHeadline,
  HeroSubheadline,
  SectionEyebrow,
} from '../../components/section';
import Link from 'next/link';
import { Button } from '../../components/ui/button';
import { getAssetUrl } from '../../utils/contentstack';
import { OptimizedImage } from '../../components/ui/optimizedImage';

export const Page: NextPage<IPageProps<VerticalHub>> = ({
  header,
  page,
  footer,
}) => {
  return (
    <>
      <Header
        pageTitle={page?.title}
        pageDescription={page?.description}
        {...header}
      />

      <Hero>
        <ResponsiveContainer className="content">
          <SectionEyebrow>{page?.hero?.eyebrow}</SectionEyebrow>
          <HeroHeadline
            dangerouslySetInnerHTML={{ __html: page?.hero?.headline || '' }}
          />
          <HeroSubheadline
            dangerouslySetInnerHTML={{ __html: page?.hero?.subheadline! }}
          />

          <div className="buttons">
            {!!page?.hero?.primary_button?.title && (
              <Link
                href={page?.hero?.primary_button?.href!}
                legacyBehavior
                passHref
              >
                <Button mType="primary">
                  {page?.hero?.primary_button?.title}
                </Button>
              </Link>
            )}
          </div>
        </ResponsiveContainer>
      </Hero>

      <VerticalsSection>
        <ResponsiveContainer>
          <div className="row">
            {page?.verticalsConnection?.edges?.map((edge) => {
              const vertical = edge?.node;

              return (
                <div key={vertical?.url} className="vertical">
                  <div className="image">
                    <OptimizedImage
                      src={getAssetUrl(vertical?.meta?.imageConnection)}
                      width={602}
                      height={1}
                    />
                  </div>

                  <h3 className="title">{vertical?.meta?.title}</h3>
                  <p className="description">{vertical?.meta?.description}</p>

                  <Link href={vertical?.url!} className="link">
                    Learn more
                  </Link>
                </div>
              );
            })}
          </div>
        </ResponsiveContainer>
      </VerticalsSection>

      <Footer sections={footer?.sections} {...page?.footer} marginTop={false} />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ params }) => {
  try {
    const { data } = await ContentStackClient().query<
      IPageProps<AllVerticalHub>
    >({
      query: PAGES_QUERY,
    });

    const page = data.page?.items?.[0] || null;

    return {
      props: { ...data, page },
    };
  } catch (e: any) {
    return { props: {} };
  }
};

export const fetchAllVerticalsUrls = async (): Promise<string[]> => {
  const { data } = await ContentStackClient().query<{
    all_vertical: AllVertical;
    all_vertical_alt: AllVerticalAlt;
  }>({
    query: gql`
      query allVerticalsUrls {
        all_vertical {
          items {
            url
          }
        }

        all_vertical_alt {
          items {
            url
          }
        }
      }
    `,
  });

  return [
    ...(data?.all_vertical?.items?.map((item) => item?.url) as string[]),
    ...(data?.all_vertical_alt?.items?.map((item) => item?.url) as string[]),
  ];
};

const PAGES_QUERY = gql`
  query verticalsQuery {
    ${HEADER_QUERY}
    ${FOOTER_QUERY}

    page: all_vertical_hub {
      items {
        title
        description
        hero {
          eyebrow
          headline
          subheadline
          primary_button {
            title
            href
          }
        }
        verticalsConnection {
          edges {
            node {
              ... on Vertical {
                url
                meta {
                  title
                  description
                  imageConnection {
                    edges {
                      node {
                        url
                      }
                    }
                  }
                }
              }
            }
          }
        }
        footer {
          headline
          subheadline
          primary_button {
            title
            href
          }
          secondary_button {
            title
            href
          }
          third_button {
            title
            href
          }
        }
      }
    }
  }
`;

const Hero = styled.section`
  padding-top: 180px;
  padding-bottom: 100px;

  .content {
    > * {
      width: 100%;
      max-width: 100%;
      margin-left: auto;
      margin-right: auto;
      text-align: center;
    }

    ${HeroSubheadline} {
      max-width: 700px;
    }
  }

  .buttons {
    display: flex;
    justify-content: center;
    gap: 24px;
  }

  ${mediaScreen('xs', 'md')} {
    min-height: auto;
    padding-top: 100px;
    padding-bottom: 40px;

    .content {
      > * {
        text-align: left;
      }
    }

    .buttons {
      flex-direction: column;
      gap: 16px;

      .mButton {
        width: 100%;
        display: flex;
      }
    }
  }
`;

const VerticalsSection = styled.section`
  background: white;
  padding-top: 100px;
  padding-bottom: 100px;

  ${mediaScreen('xs', 'md')} {
    padding-top: 60px;
    padding-bottom: 60px;
  }

  .row {
    display: flex;
    gap: 32px;
    flex-wrap: wrap;
  }

  .vertical {
    overflow: hidden;
    position: relative;
    border-radius: 8px;
    background: ${(p) => p.theme.colors.slate1};
    padding: 32px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 24px;
    cursor: pointer;
    width: calc((100% - 48px) / 2);

    .link {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 1;
      opacity: 0;
    }

    .image {
      flex: 1;
      overflow: hidden;
      border-radius: 8px;

      div,
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .title {
      font-size: 24px;
      font-weight: 500;
      line-height: 1.5;
      margin: 0;
    }

    .description {
      font-size: 16px;
      color: ${(p) => p.theme.colors.teal};
      margin: 0;
    }

    &:hover {
      .image,
      .title,
      .description {
        opacity: 0.7;
      }
    }
  }

  ${mediaScreen('xs', 'md')} {
    padding-top: 40px;
    padding-bottom: 40px;

    .row {
      flex-wrap: wrap;
      gap: 30px;
    }

    .vertical {
      width: 100%;
      padding: 24px;
      gap: 16px;
    }
  }
`;

export default Page;
